import server from '../config';
import store from '../store';
import setSDK from '../sdk';
import Vue from 'vue';
import VueRouter from 'vue-router';

import axios from 'axios';
import VueJwtDecode from 'vue-jwt-decode';
import reports from '@/router/reports';
import goals from '@/router/goals';
import settings from '@/router/settings';
import reviewCycle from '@/router/review';
import talentManagement from '@/router/talent-management';

const Login = () => import(/* webpackChunkName: "login" */ '@/views/public/Login');
const SSOCallback = () => import(/* webpackChunkName: "login" */ '@/views/public/SSOCallback');
const Consultant = () => import(/* webpackChunkName: "consultant" */ '@/views/public/Consultant');
const Landing = () => import(/* webpackChunkName: "consultant" */ '@/views/public/Landing');
const PermissionFailed = () => import(/* webpackChunkName: "permission-failed" */ '@/views/public/PermissionFailed');
const Home = () => import(/* webpackChunkName: "home" */ '@/views/employee/Root');
const HomeIndex = () => import(/* webpackChunkName: "home-index" */ '@/views/employee/Index');

Vue.use(VueRouter);

const routes = [
  {
    path: '/',
    redirect: '/home',
  },
  {
    path: '/login',
    name: 'login',
    meta: { withoutNavbar: true, withoutFooter: true },
    component: Login,
  },
  {
    path: '/sso-callback',
    name: 'sso-callback',
    meta: { withoutNavbar: true, withoutFooter: true },
    component: SSOCallback,
  },
  {
    path: '/redirect-login',
    name: 'token',
    beforeEnter: async (to, from, next) => {
      store.dispatch('resetCookies');
      localStorage.clear();
      try {
        const token = to.query.key;
        const { redirect } = to.query;
        Vue.$cookies.set('token', token);
        const response = await axios.get(`${server.endPoint}/me`);
        if (response.data.status_code && response.data.status_code === 200) {
          setSDK(token);

          if (redirect) {
            next({ name: redirect });
          } else {
            next({ name: 'home' });
          }
        } else {
          Vue.$cookies.remove('token');
          next({ name: 'login' });
        }
      } catch (error) {
        Vue.$cookies.remove('token');
        next({ name: 'login' });
      }
    },
  },
  {
    path: '/consultant',
    name: 'consultant_index',
    meta: { requiresAuth: true, withoutNavbar: true },
    component: Consultant,
  },
  {
    path: '/landing',
    name: 'landing_page',
    meta: { withoutNavbar: true },
    component: Landing,
  },
  {
    path: '/permission-failed',
    name: 'permission_failed',
    meta: { withoutNavbar: true },
    component: PermissionFailed,
  },
  {
    path: '/home',
    meta: { requiresAuth: true },
    component: Home,
    children: [
      {
        path: '',
        name: 'home',
        component: HomeIndex,
      },
      {
        path: 'subordinate/:slug',
        name: 'home_subordinate',
        component: HomeIndex,
        props: { uuid: '', name: '', parent: '', subordinate: [] },
      },
    ],
  },
  ...reports,
  ...reviewCycle,
  ...goals,
  ...talentManagement,
  ...settings,
  {
    path: '*',
    redirect: '/',
  },
];

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,

  scrollBehavior() {
    return { x: 0, y: 0 };
  },
});

router.beforeEach((to, from, next) => {
  // this route requires auth, check if logged in
  // if not, redirect to login page.

  if (to.matched.some(record => record.meta.requiresAuth)) {
    const token = Vue.$cookies.get('token');
    if (token) {
      const consultantData = Vue.$cookies.get('consultant');
      store.dispatch('storeConsultantData', consultantData);
      const tokenData = VueJwtDecode.decode(token);
      store.dispatch('storeTokenData', tokenData);

      const isa = Vue.$cookies.get('isa-consultant') || tokenData.isa;
      store.dispatch('storeSuperAdminData', isa);

      setSDK(token, consultantData);

      // IF in pending action pages, use total for filtered company, otherwise use user's company
      if (to.path.includes('/reviews/performance-review/pending-action/')) {
        store.commit('SET_SIDEBAR_NOTIF', { pending_action: Vue.$cookies.get('total-pending-task-filtered-company') }, { root: true });
      } else {
        store.commit('SET_SIDEBAR_NOTIF', { pending_action: Vue.$cookies.get('total-pending-task') }, { root: true });
      }

      const checkCompanyConsultant = async () => {
        try {
          const url = `${server.nonCoreEndPoint}/shared/consultant/is-company-consultant/${consultantData.company_id}`;
          const res = await axios.get(url);

          if (res.data === 1) {
            next();
          } else {
            Vue.$cookies.remove('consultant');
            next({ name: 'consultant_index' });
          }
        } catch (e) {
          Vue.$cookies.remove('consultant');
          next({ name: 'consultant_index' });
        }
      };

      if (to.matched.some(record => record.meta.requiresAdmin) && (!isa || isa === 'false')) {
        next({ name: 'home' });
      } else if (consultantData && !(to.name === 'consultant_index')) {
        checkCompanyConsultant();
      } else {
        next();
      }
    } else {
      // set query params referrer for after login redirection
      next({ name: 'login', query: { referrer: to.path, companyId: to.query.company_id } });
    }
  } else {
    next(); // if no required auth, next()
  }
});

router.beforeEach(async (to, from, next) => {
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth);

  // check ess permissions
  if (Vue.$cookies.get('token') && requiresAuth) store.dispatch('goals/getEssPermissions', server.coreBaseURL);

  // check permision for succession plan
  const talentManagementRoutes = ['succession-plan', 'competency-management', 'individual-development-plan', 'talent-directory'];
  if (talentManagementRoutes.some(routeName => to.path.includes(routeName))) {
    const cid = store.state.consultant?.company_id || store.state.userData?.company_id;
    await store.dispatch('checkSuccessionPlanPermission', cid);
    const isAdvancePR = store.state.canAccessSuccessionPlan;
    const isSuperAdmin = store.state.superAdminData === 'false' ? false : store.state.superAdminData;
    if (isAdvancePR) {
      if (to.path.includes('individual-development-plan')) {
        next();
      } else if (isSuperAdmin) {
        next();
      } else {
        next({ path: '/', query: { talent: 'forbidden' } });
      }
    } else {
      next({ path: '/talent-management-landing' });
    }
  } else {
    next();
  }
});

export default router;
